/**
 * Event deduplication utility for analytics tracking.
 *
 * This module provides functionality to prevent duplicate analytics events
 * from being tracked within specified time windows.
 */

import { getDebounceConfig } from '../config/debounce-config.js';
import type { AnalyticsEvent } from '../types/index.js';

interface EventKey {
  name: string;
  category: string;
  /** Serialized key properties for deduplication */
  keyProps: string;
}

interface TrackedEvent {
  key: EventKey;
  timestamp: number;
  /** Time when this event should be removed from tracking */
  expiresAt: number;
}

/**
 * In-memory store for tracking recent events
 */
class EventDeduplicationStore {
  private events = new Map<string, TrackedEvent>();
  private cleanupInterval: NodeJS.Timeout | null = null;
  private readonly CLEANUP_INTERVAL = 30000; // 30 seconds

  constructor() {
    this.startCleanup();
  }

  /**
   * Generate a unique key for an event
   */
  private generateEventKey(event: AnalyticsEvent): string {
    const keyProps = this.extractKeyProperties(event);
    return `${event.name}:${event.category}:${keyProps}`;
  }

  /**
   * Extract key properties from event for deduplication
   * Only include properties that should make events unique
   */
  private extractKeyProperties(event: AnalyticsEvent): string {
    if (!event.properties) {
      return '';
    }

    // Define which properties should be considered for deduplication
    const keyProperties = [
      'deviceId',
      'device_id',
      'agentId',
      'agent_id',
      'toolName',
      'quickShotType',
      'shotType',
      'presetId',
      'groupId',
      'sourceId',
      'planType',
      'billingFrequency',
      'paymentMethodType',
      'invoiceId',
      'userId',
      'user_id',
      'organizationId',
      'studioId',
      'studio_id',
      'bridgeId',
      'controllerId',
      'commandKey',
      'command_key'
    ];

    const relevantProps: Record<string, unknown> = {};

    for (const prop of keyProperties) {
      if (prop in event.properties) {
        relevantProps[prop] = event.properties[prop];
      }
    }

    // Sort keys for consistent serialization
    const sortedKeys = Object.keys(relevantProps).sort();
    const sortedProps = sortedKeys.reduce(
      (acc, key) => {
        acc[key] = relevantProps[key];
        return acc;
      },
      {} as Record<string, unknown>
    );

    return JSON.stringify(sortedProps);
  }

  /**
   * Check if an event is a duplicate within the deduplication window
   */
  isDuplicate(event: AnalyticsEvent): boolean {
    const config = getDebounceConfig(event.name);

    // If deduplication is disabled for this event
    if (!config.enabled || config.dedupe <= 0) {
      return false;
    }

    const eventKey = this.generateEventKey(event);
    const trackedEvent = this.events.get(eventKey);
    const now = Date.now();

    // If no previous event or it has expired
    if (!trackedEvent || now > trackedEvent.expiresAt) {
      return false;
    }

    // Event is within deduplication window
    return true;
  }

  /**
   * Track an event to prevent future duplicates
   */
  trackEvent(event: AnalyticsEvent): void {
    const config = getDebounceConfig(event.name);

    // If deduplication is disabled for this event
    if (!config.enabled || config.dedupe <= 0) {
      return;
    }

    const eventKey = this.generateEventKey(event);
    const now = Date.now();
    const expiresAt = now + config.dedupe;

    const trackedEvent: TrackedEvent = {
      key: {
        name: event.name,
        category: event.category,
        keyProps: this.extractKeyProperties(event)
      },
      timestamp: now,
      expiresAt
    };

    this.events.set(eventKey, trackedEvent);
  }

  /**
   * Get statistics about tracked events
   */
  getStats(): {
    totalTracked: number;
    activeEvents: number;
    expiredEvents: number;
  } {
    const now = Date.now();
    let activeEvents = 0;
    let expiredEvents = 0;

    for (const event of this.events.values()) {
      if (now <= event.expiresAt) {
        activeEvents++;
      } else {
        expiredEvents++;
      }
    }

    return {
      totalTracked: this.events.size,
      activeEvents,
      expiredEvents
    };
  }

  /**
   * Start periodic cleanup of expired events
   */
  private startCleanup(): void {
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, this.CLEANUP_INTERVAL);
  }

  /**
   * Remove expired events from memory
   */
  private cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, event] of this.events.entries()) {
      if (now > event.expiresAt) {
        keysToDelete.push(key);
      }
    }

    for (const key of keysToDelete) {
      this.events.delete(key);
    }

    // Log cleanup stats in development
    if (process.env.NODE_ENV === 'development' && keysToDelete.length > 0) {
      console.debug(`[Analytics] Cleaned up ${keysToDelete.length} expired events`);
    }
  }

  /**
   * Clear all tracked events (useful for testing)
   */
  clear(): void {
    this.events.clear();
  }

  /**
   * Stop cleanup interval (useful for cleanup)
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.clear();
  }
}

/**
 * Global event deduplication store instance
 */
const eventStore = new EventDeduplicationStore();

/**
 * Check if an analytics event is a duplicate
 */
export function isDuplicateEvent(event: AnalyticsEvent): boolean {
  return eventStore.isDuplicate(event);
}

/**
 * Track an analytics event to prevent future duplicates
 */
export function trackEventForDeduplication(event: AnalyticsEvent): void {
  eventStore.trackEvent(event);
}

/**
 * Get deduplication statistics
 */
export function getDeduplicationStats() {
  return eventStore.getStats();
}

/**
 * Clear all tracked events (for testing)
 */
export function clearTrackedEvents(): void {
  eventStore.clear();
}

/**
 * Destroy the event store (for cleanup)
 */
export function destroyEventStore(): void {
  eventStore.destroy();
}

/**
 * Process an event through the deduplication system
 * Returns true if the event should be tracked, false if it's a duplicate
 */
export function processEventDeduplication(event: AnalyticsEvent): boolean {
  // Check if it's a duplicate
  if (isDuplicateEvent(event)) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Analytics] Blocked duplicate event: ${event.name}`, {
        category: event.category,
        properties: event.properties
      });
    }
    return false;
  }

  // Track the event for future deduplication
  trackEventForDeduplication(event);
  return true;
}
